// v3.18-1336 APP-AUTO-001 — Application Assignment Gate.
//
// Persistent bottom-right banner + blocking modal that surfaces every
// application awaiting operator resolution.  Polls
// /application-assignments/pending every 45s.  Auto-opens the modal
// for the top item; the operator can also click any of the queued
// rows to jump straight to them.
//
// The banner (and modal) render for internal operators, managers,
// directors, and admins — i.e., anyone who can legitimately resolve
// ownership.  External-company users still see only their own
// company's queue (scoped by the backend list endpoint).
//
// v3.21-1815 — Each queued row now surfaces a direct OPEN CLIENT /
// OPEN INTAKE action (using canonical IDs — never a name match).
// The action navigates immediately to the exact record with the
// Ownership Information tab pre-selected.
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Building2, AlertCircle, ExternalLink } from 'lucide-react';
import { ApplicationAssignmentModal } from './ApplicationAssignmentModal';

const POLL_MS = 45_000;

const ELIGIBLE_ROLES = new Set([
  'ptc_admin', 'ptc_director', 'admin', 'company_director', 'manager',
]);

export function ApplicationAssignmentGate({ currentUser }) {
  const [pending, setPending] = useState([]);
  const [openId, setOpenId]   = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const role = String(currentUser?.role || '').toLowerCase();
  const canResolve = ELIGIBLE_ROLES.has(role)
    || (currentUser?.company_type || '').toLowerCase() === 'internal';

  const load = useCallback(async () => {
    if (!canResolve) return;
    try {
      const { data } = await api.get('/application-assignments/pending');
      setPending(data?.assignments || []);
    } catch { /* silent — never block layout */ }
  }, [canResolve]);

  useEffect(() => {
    if (!canResolve) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [canResolve, load]);

  const openExactRecord = useCallback(async (a) => {
    // v3.21-1815 — resolve the exact click-through target using
    // canonical IDs.  Falls back to the resolution modal only when
    // neither a Client nor an Intake exists yet.
    try {
      const { data } = await api.get(`/application-assignments/${a.id}/target`);
      const route = data?.target?.route;
      if (route) { navigate(route); return; }
    } catch (_e) { /* fall through to modal */ }
    setOpenId(a.id);
  }, [navigate]);

  if (!canResolve || pending.length === 0) return null;
  const top = pending[0];

  return (
    <>
      <div
        // v3.21-1815 — positioned bottom-right so it never covers
        // the left sidebar or blocks primary navigation.
        className="fixed bottom-4 right-4 z-[60] max-w-[400px] bg-amber-50 border border-amber-300 shadow-xl rounded-lg"
        data-testid="app-assign-banner"
      >
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left"
          data-testid="app-assign-banner-toggle"
        >
          <AlertCircle className="text-amber-700" size={16} />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-amber-900">
              {pending.length} application{pending.length === 1 ? '' : 's'} awaiting ownership resolution
            </div>
            <div className="text-[11px] text-amber-800 truncate">
              {top.hint || top.origin_rep_name || top.origin_company_name}
            </div>
          </div>
        </button>
        {!collapsed && (
          <div className="px-3 pb-3 space-y-1 max-h-[280px] overflow-y-auto">
            {pending.map(a => (
              <div
                key={a.id}
                className="w-full flex items-center gap-2 px-2 py-1.5 bg-white rounded border border-amber-200"
                data-testid={`app-assign-queue-${a.id}`}
              >
                <Building2 size={12} className="text-amber-700 shrink-0" />
                <div className="flex-1 min-w-0 truncate text-[12px]">
                  <b>{a.origin_rep_name || '(no rep)'}</b>
                  <span className="text-slate-500"> — {a.resolved_company_name || a.origin_company_name || 'NO COMPANY'}</span>
                </div>
                <span className={`text-[10px] uppercase font-bold ${a.resolution_status === 'multi_match' ? 'text-orange-700' : 'text-red-700'}`}>
                  {a.resolution_status.replace(/_/g, ' ')}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openExactRecord(a); }}
                  className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-amber-900 bg-amber-100 hover:bg-amber-200 rounded border border-amber-300"
                  data-testid={`app-assign-open-${a.id}`}
                  title="Open the exact Client or Intake"
                >
                  <ExternalLink size={11} />
                  Open
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOpenId(a.id); }}
                  className="ml-1 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 rounded"
                  data-testid={`app-assign-resolve-${a.id}`}
                  title="Open the ownership resolution modal"
                >
                  Resolve
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ApplicationAssignmentModal
        assignmentId={openId}
        isOpen={!!openId}
        onResolved={(payload) => {
          setOpenId(null);
          load();
          // v3.21-1815 — after resolution jump the operator straight
          // to the newly assigned Client so the lead never
          // "disappears".
          const clientId = payload && (payload.resolved_client_id || payload.target?.id);
          const route = payload && payload.target && payload.target.route;
          if (clientId) navigate(`/clients/${clientId}?tab=ownership`);
          else if (route) navigate(route);
        }}
        onClose={() => { setOpenId(null); load(); }}
      />
    </>
  );
}

export default ApplicationAssignmentGate;
